'use client';

import React, { useEffect, useState, useContext } from 'react';
import { supabase } from '../../supabaseClient';
import { AuthContext } from '../../context/AuthContext';
import { CheckIcon, ArrowPathIcon, ExclamationTriangleIcon, MapPinIcon, CheckCircleIcon } from '@heroicons/react/24/solid';

export default function OnStationModal({
  isOpen,
  onClose,
  selectedMeasurements,
  onSubmit,
  onCloseWithReset,
}) {
  const { user } = useContext(AuthContext);
  const [organization, setOrganization] = useState(null);
  const [validationResults, setValidationResults] = useState([]);
  const [unresolvedMeasurements, setUnresolvedMeasurements] = useState([]);
  const [selectedProjects, setSelectedProjects] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [measurementsPerPage] = useState(5);
  const [isValidating, setIsValidating] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [selectAllProject, setSelectAllProject] = useState('');
  const [previewMode, setPreviewMode] = useState(false);
  const [submissionSuccess, setSubmissionSuccess] = useState(false);
  const [projectSummary, setProjectSummary] = useState([]);
  const [exportBatchId, setExportBatchId] = useState(null);
  const { session } = useContext(AuthContext);

  // Fetch organization and validate projects on modal open
  useEffect(() => {
    const initializeModal = async () => {
      if (!isOpen || !user) return;

      setIsValidating(true);
      setError(null);
      setValidationResults([]);
      setUnresolvedMeasurements([]);
      setSelectedProjects({});
      setCurrentPage(1);
      setSelectAllProject('');
      setPreviewMode(false);
      setSubmissionSuccess(false);
      setProjectSummary([]);
      setExportBatchId(null);

      try {
        // Step 1: Fetch Organization
        const { data: orgData, error: orgError } = await supabase
          .from('organization_members')
          .select('organizations(id, name)')
          .eq('user_id', user.id)
          .single();

        if (orgError) {
          throw new Error('Could not fetch organization.');
        }

        const orgId = orgData?.organizations?.id || null;
        setOrganization(orgId);

        if (!orgId) {
          throw new Error('User is not part of an organization.');
        }

        // Step 2: Validate Projects using Edge Function
        if (!session) {
          throw new Error('Authentication session not found.');
        }

        const response = await fetch(
          `https://zccgzqkkpmqpawelftbw.supabase.co/functions/v1/validate-onstation-projects`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              user_id: user.id,
              organization_id: orgId,
              measurements: selectedMeasurements.map((m) => ({
                id: m.id,
                GpsCoordinates: m.GpsCoordinates,
              })),
            }),
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          console.error('Edge function error:', errorText);
          throw new Error(`Failed to validate projects: ${response.status} ${response.statusText}`);
        }

        const result = await response.json();
        setValidationResults(result.results);

        // Step 3: Filter unresolved measurements (multiple_projects)
        const unresolved = result.results.filter(
          (r) => r.status === 'multiple_projects'
        );
        setUnresolvedMeasurements(unresolved);

        // Step 4: Auto-assign projects for single_project measurements
        const newSelectedProjects = {};
        result.results.forEach((r) => {
          if (r.status === 'single_project' && r.projects.length === 1) {
            newSelectedProjects[r.measurement_id] = r.projects[0].code;
          }
        });
        setSelectedProjects(newSelectedProjects);

        // Step 5: Generate project summary
        const projectCounts = {};
        const projectDetails = {};

        Object.keys(newSelectedProjects).forEach(measurementId => {
          const projectCode = newSelectedProjects[measurementId];
          projectCounts[projectCode] = (projectCounts[projectCode] || 0) + 1;

          if (!projectDetails[projectCode]) {
            const measurement = result.results.find(r => r.measurement_id === measurementId);
            if (measurement && measurement.projects.length > 0) {
              const project = measurement.projects.find(p => p.code === projectCode);
              if (project) {
                projectDetails[projectCode] = {
                  name: project.name,
                  state: project.state,
                };
              }
            }
          }
        });

        const summary = Object.keys(projectCounts).map(code => ({
          code,
          name: projectDetails[code]?.name || 'Unknown Project',
          state: projectDetails[code]?.state || 'N/A',
          count: projectCounts[code],
        }));

        setProjectSummary(summary);
      } catch (err) {
        console.error('Error validating projects:', err);
        setError(err.message || 'Failed to validate OnStation projects.');
      } finally {
        setIsValidating(false);
      }
    };

    if (isOpen) initializeModal();
  }, [isOpen, user, selectedMeasurements, session]);

  // Auto-close the modal after showing success message
  useEffect(() => {
    if (submissionSuccess) {
      const timer = setTimeout(() => {
        onCloseWithReset();
      }, 3000); // Close after 3 seconds

      return () => clearTimeout(timer);
    }
  }, [submissionSuccess, onCloseWithReset]);

  // Update project summary whenever selectedProjects changes
  useEffect(() => {
    if (validationResults.length === 0) return;

    const projectCounts = {};
    const projectDetails = {};

    Object.keys(selectedProjects).forEach(measurementId => {
      const projectCode = selectedProjects[measurementId];
      projectCounts[projectCode] = (projectCounts[projectCode] || 0) + 1;

      if (!projectDetails[projectCode]) {
        const measurement = validationResults.find(r => r.measurement_id === measurementId);
        if (measurement && measurement.projects.length > 0) {
          const project = measurement.projects.find(p => p.code === projectCode);
          if (project) {
            projectDetails[projectCode] = {
              name: project.name,
              state: project.state,
            };
          }
        }
      }
    });

    const summary = Object.keys(projectCounts).map(code => ({
      code,
      name: projectDetails[code]?.name || 'Unknown Project',
      state: projectDetails[code]?.state || 'N/A',
      count: projectCounts[code],
    }));

    setProjectSummary(summary);
  }, [selectedProjects, validationResults]);

  // Handle project selection for individual measurements
  const handleSelectProject = (measurementId, projectCode) => {
    setSelectedProjects((prev) => ({
      ...prev,
      [measurementId]: projectCode,
    }));
  };

  // Handle "Select All" project selection
  const handleSelectAllProject = (projectCode) => {
    setSelectAllProject(projectCode);
    const newSelectedProjects = { ...selectedProjects };
    unresolvedMeasurements.forEach((m) => {
      if (m.status === 'multiple_projects') {
        newSelectedProjects[m.measurement_id] = projectCode;
      }
    });
    setSelectedProjects(newSelectedProjects);
  };

  // Handle submission using submit_onstation_measurements Edge Function
  const handleSubmit = async () => {
    if (!organization) {
      setError('Organization not found.');
      setIsSubmitting(false);
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const measurementsToSubmit = selectedMeasurements
        .filter((m) => selectedProjects[m.id])
        .map((m) => ({
          id: m.id,
          GpsCoordinates: m.GpsCoordinates,
          LocalTimeOfMeasurement: m.LocalTimeOfMeasurement,
          Density: m.Density,
          Compaction: m.Compaction,
          SurfaceTemp: m.SurfaceTemp,
          projectCode: selectedProjects[m.id],
        }));

      if (measurementsToSubmit.length === 0) {
        throw new Error('No measurements can be submitted. All measurements may lack compatible projects.');
      }

      const response = await fetch(
        `https://zccgzqkkpmqpawelftbw.supabase.co/functions/v1/submit_onstation_measurements`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            user_id: user.id,
            organization_id: organization,
            measurements: measurementsToSubmit,
            export_batch_id: exportBatchId,
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to submit measurements: ${response.status} ${response.statusText} - ${errorText}`);
      }

      const result = await response.json();
      setExportBatchId(result.export_batch_id);

      // Set submission success state before triggering the next function
      setSubmissionSuccess(true);

      // Notify parent component about the successful submission
      if (onSubmit) {
        onSubmit(measurementsToSubmit);
      }

      // Trigger process_onstation_measurements with the export_batch_id
      try {
        await fetch(
          `https://zccgzqkkpmqpawelftbw.supabase.co/functions/v1/process_onstation_measurements`,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${session.access_token}`,
            },
            body: JSON.stringify({
              export_batch_id: result.export_batch_id,
            }),
          }
        );
        // No need to wait for this process to complete to show success to user
      } catch (processError) {
        // Log the error but don't change UI state since initial submission was successful
        console.error('Error processing measurements (background task):', processError);
      }
    } catch (err) {
      console.error('Error submitting measurements:', err);
      setError(err.message || 'Failed to send measurements to OnStation.');
      setSubmissionSuccess(false); // Ensure success state is false on error
    } finally {
      setIsSubmitting(false);
    }
  };

  // Pagination for unresolved measurements
  const indexOfLastMeasurement = currentPage * measurementsPerPage;
  const indexOfFirstMeasurement = indexOfLastMeasurement - measurementsPerPage;
  const currentUnresolved = unresolvedMeasurements.slice(indexOfFirstMeasurement, indexOfLastMeasurement);
  const totalPages = Math.ceil(unresolvedMeasurements.length / measurementsPerPage);

  if (!isOpen) return null;

  // Count of total measurements assigned to projects
  const totalAssignedCount = Object.keys(selectedProjects).length;
  const totalNoProjectCount = validationResults.filter(r => r.status === 'no_projects').length;

  const isAllUnmappable = validationResults.length > 0 && validationResults.every((r) => {
    return (
      r.status === 'no_projects' ||
      (r.status === 'error' && r.message?.includes('no projects found that match the search criteria'))
    );
  });
  

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-800 bg-opacity-75">
      <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold mb-4">Send to OnStation</h2>
  
        {error ? (
          <div className="text-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <p className="text-lg font-medium text-amber-700 mb-2">{error}</p>
            <button
              onClick={onCloseWithReset}
              className="inline-flex w-full justify-center rounded-md bg-gray-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
            >
              Close
            </button>
          </div>
        ) : isValidating ? (
          <div className="text-center">
            <ArrowPathIcon className="h-12 w-12 text-gray-500 animate-spin mx-auto mb-4" />
            <p className="text-lg text-gray-700">Validating OnStation projects...</p>
          </div>
        ) : submissionSuccess ? (
          <div className="text-center">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <CheckIcon className="h-6 w-6 text-green-600" />
            </div>
            <h3 className="mt-3 text-base font-semibold text-gray-900 sm:mt-5">Submission Received!</h3>
            <p className="text-sm text-gray-500 mb-2">
              {selectedMeasurements.filter((m) => selectedProjects[m.id]).length} Measurement
              {selectedMeasurements.filter((m) => selectedProjects[m.id]).length !== 1 ? 's' : ''} have been submitted to OnStation.
            </p>
            <div className="mt-3 bg-blue-50 p-4 rounded-lg border border-blue-200">
              <div className="flex items-start">
                <ArrowPathIcon className="h-5 w-5 text-blue-500 mr-2 mt-0.5" />
                <p className="text-sm text-blue-700">
                  Your measurements are being processed by OnStation. Check the <span className="font-semibold">Export History</span> page for updates.
                </p>
              </div>
            </div>
          </div>
        ) : isAllUnmappable ? (
          <div className="text-center">
            <ExclamationTriangleIcon className="h-12 w-12 text-amber-500 mx-auto mb-4" />
            <p className="text-lg font-medium text-amber-700 mb-2">
              No measurements are compatible with OnStation projects.
            </p>
            <p className="text-sm text-gray-500 mb-4">
              None of the selected measurements could be matched to any OnStation projects.
            </p>
            <button
              onClick={onCloseWithReset}
              className="inline-flex w-full justify-center rounded-md bg-gray-400 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-gray-500"
            >
              Close
            </button>
          </div>
        )
         : unresolvedMeasurements.length > 0 ? (
          <div>
            {/* Project Summary Cards */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Projects Summary</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-semibold text-blue-700">Total Measurements</p>
                      <p className="text-2xl font-bold text-blue-800">{selectedMeasurements.length}</p>
                    </div>
                    <MapPinIcon className="h-8 w-8 text-blue-500" />
                  </div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border border-blue-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm font-semibold text-green-700">Assigned to Projects</p>
                      <p className="text-2xl font-bold text-green-800">{totalAssignedCount}</p>
                    </div>
                    <CheckCircleIcon className="h-8 w-8 text-green-500" />
                  </div>
                </div>
              </div>
  
              {projectSummary.length > 0 && (
                <div className="mt-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-2">Measurements per Project</h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {projectSummary.map(project => (
                      <div key={project.code} className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-yellow-800">{project.name}</p>
                            <p className="text-xs text-yellow-600">{project.state}</p>
                          </div>
                          <div className="bg-yellow-200 px-3 py-1 rounded-full">
                            <span className="text-sm font-semibold text-yellow-800">{project.count}</span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
  
            {unresolvedMeasurements.length > 0 && (
              <div className="mt-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3">
                  Measurements Requiring Resolution ({unresolvedMeasurements.length})
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  These measurements are located in multiple OnStation projects. Please select which project to assign them to.
                </p>
  
                {/* Apply to All Selector */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Apply one project to all unresolved measurements:
                  </label>
                  <select
                    value={selectAllProject}
                    onChange={(e) => handleSelectAllProject(e.target.value)}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                  >
                    <option value="">Select a project</option>
                    {[...new Set(unresolvedMeasurements
                      .flatMap((m) => m.projects.map((p) => p.code)))].map((code) => {
                      const project = unresolvedMeasurements
                        .find((m) => m.projects.some(p => p.code === code))
                        ?.projects.find((p) => p.code === code);
                      return (
                        <option key={code} value={code}>
                          {project.name} ({project.state})
                        </option>
                      );
                    })}
                  </select>
                </div>
  
                {/* Unresolved Measurements Table */}
                <table className="min-w-full border-collapse border border-gray-200 mb-4">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="border border-gray-200 px-4 py-2 text-left text-sm font-semibold text-gray-900">
                        Description
                      </th>
                      <th className="border border-gray-200 px-4 py-2 text-left text-sm font-semibold text-gray-900">
                        Project
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentUnresolved.map((result) => {
                      const measurement = selectedMeasurements.find((m) => m.id === result.measurement_id);
                      return (
                        <tr key={result.measurement_id} className="hover:bg-gray-50">
                          <td className="border border-gray-200 px-4 py-2 text-sm text-gray-700">
                            {measurement?.Description1 || 'N/A'}
                          </td>
                          <td className="border border-gray-200 px-4 py-2 text-sm text-gray-700">
                            <select
                              value={selectedProjects[result.measurement_id] || ''}
                              onChange={(e) => handleSelectProject(result.measurement_id, e.target.value)}
                              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-yellow-500 focus:ring-yellow-500 sm:text-sm"
                              disabled={!!selectAllProject}
                            >
                              <option value="">Select a project</option>
                              {result.projects.map((project) => (
                                <option key={project.code} value={project.code}>
                                  {project.name} ({project.state})
                                </option>
                              ))}
                            </select>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
  
                {/* Pagination */}
                {totalPages > 1 && (
                  <nav className="flex justify-between mt-4">
                    <button
                      onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                      disabled={currentPage === 1}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <span>
                      Page {currentPage} of {totalPages}
                    </span>
                    <button
                      onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                      disabled={currentPage === totalPages}
                      className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md disabled:opacity-50"
                    >
                      Next
                    </button>
                  </nav>
                )}
              </div>
            )}
  
            {/* No Projects Found Info */}
            {totalNoProjectCount > 0 && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  <span className="font-medium">{totalNoProjectCount}</span> measurement{totalNoProjectCount !== 1 ? 's' : ''} could not be matched to any OnStation project and will be skipped.
                </p>
              </div>
            )}
  
            {/* Action Buttons */}
            <div className="flex justify-end space-x-2 mt-6">
              <button
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center justify-center"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <ArrowPathIcon className="h-5 w-5 animate-spin mr-2" />
                    Submitting
                  </>
                ) : (
                  'Submit'
                )}
              </button>
            </div>
          </div>
        ) : selectedMeasurements.filter((m) => selectedProjects[m.id]).length > 0 ? (
          <div>
  
            {/* Project summary cards */}
            <div className="mb-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Projects</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                {projectSummary.map(project => (
                  <div key={project.code} className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-yellow-800">{project.name}</p>
                        <p className="text-xs text-yellow-600">{project.state}</p>
                      </div>
                      <div className="bg-yellow-200 px-3 py-1 rounded-full">
                        <span className="text-sm font-semibold text-yellow-800">{project.count}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
  
            <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 mb-6">
              <h3 className="text-sm font-semibold text-gray-700 mb-2">Measurements Preview</h3>
              <table className="min-w-full border-collapse border border-gray-200">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border border-gray-200 px-4 py-2 text-left text-sm font-semibold text-gray-900">
                      Description
                    </th>
                    <th className="border border-gray-200 px-4 py-2 text-left text-sm font-semibold text-gray-900">
                      Project
                    </th>
                    <th className="border border-gray-200 px-4 py-2 text-left text-sm font-semibold text-gray-900">
                      Density
                    </th>
                    <th className="border border-gray-200 px-4 py-2 text-left text-sm font-semibold text-gray-900">
                      Compaction
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {selectedMeasurements
                    .slice(0,3)
                    .filter((m) => selectedProjects[m.id])
                    .map((measurement) => {
                      const project = validationResults
                        .find((r) => r.measurement_id === measurement.id)
                        ?.projects.find((p) => p.code === selectedProjects[measurement.id]);
                      return (
                        <tr key={measurement.id} className="hover:bg-gray-50">
                          <td className="border border-gray-200 px-4 py-2 text-sm text-gray-700">
                            {measurement.Description1 || 'N/A'}
                          </td>
                          <td className="border border-gray-200 px-4 py-2 text-sm text-gray-700">
                            {project ? `${project.name} (${project.state})` : 'N/A'}
                          </td>
                          <td className="border border-gray-200 px-4 py-2 text-sm text-gray-700">
                            {measurement.Density?.toFixed(1) || 'N/A'}
                          </td>
                          <td className="border border-gray-200 px-4 py-2 text-sm text-gray-700">
                            {measurement.Compaction?.toFixed(1) || 'N/A'}
                          </td>
                        </tr>
                      );
                    })}
                </tbody>
              </table>
            </div>
  
            {/* No Projects Found Info */}
            {totalNoProjectCount > 0 && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
                <p className="text-sm text-gray-700">
                  <span className="font-medium">{totalNoProjectCount}</span> measurement{totalNoProjectCount !== 1 ? 's' : ''} could not be matched to any OnStation project and will be skipped.
                </p>
              </div>
            )}
  
            {/* Action Buttons */}
            <div className="flex justify-end space-x-2 mt-6">
              <button
                className="px-4 py-2 bg-gray-300 text-gray-700 rounded-md hover:bg-gray-400 transition-colors"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center justify-center"
                onClick={handleSubmit}
                disabled={isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <ArrowPathIcon className="h-5 w-5 animate-spin mr-2" />
                    Submit
                  </>
                ) : (
                  'Submit'
                )}
              </button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}